import React from 'react';
import { Shield, Lock, Eye, UserCheck, Database, Phone, Mail, MapPin } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <Shield className="w-16 h-16 text-orange-600 mr-4" />
              <h1 className="text-4xl font-bold text-gray-900">Privacy Policy</h1>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We value your trust and are committed to safeguarding your personal and health information. 
              This Privacy Policy explains how we collect, use, and protect your data.
            </p>
            <div className="mt-4 text-sm text-gray-500">
              Effective Date: 27-09-2025
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm border p-8 space-y-8">
          
          {/* 1. Data Collection */}
          <section>
            <div className="flex items-center mb-4">
              <Database className="w-6 h-6 text-orange-600 mr-3" />
              <h2 className="text-2xl font-semibold text-gray-900">1. Data Collection</h2>
            </div>
            <div className="text-gray-700">
              <p className="mb-4">When you use our services, we may collect:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Basic personal details (name, age, gender, phone number, email, address).</li>
                <li>Account details (user ID, password).</li>
                <li>Health-related entries (e.g., blood sugar, blood pressure, cholesterol, BMI, lab test preferences).</li>
                <li>Location data (GPS or pincode, if you choose to share it for serviceability).</li>
              </ul>
            </div>
          </section>

          {/* 2. Purpose of Use */}
          <section>
            <div className="flex items-center mb-4">
              <UserCheck className="w-6 h-6 text-orange-600 mr-3" />
              <h2 className="text-2xl font-semibold text-gray-900">2. Purpose of Use</h2>
            </div>
            <div className="text-gray-700">
              <p className="mb-4">Your data is used only for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Delivering healthcare-related services (pharmacy orders, lab bookings, doctor appointments, vitals tracking).</li>
                <li>Managing your account, addresses, and subscription status.</li>
                <li>Providing customer support and service updates.</li>
              </ul>
              <p className="mt-4 font-medium">We do not use your information for any unlawful purpose.</p>
            </div>
          </section>

          {/* 3. Data Sharing */}
          <section>
            <div className="flex items-center mb-4">
              <Eye className="w-6 h-6 text-orange-600 mr-3" />
              <h2 className="text-2xl font-semibold text-gray-900">3. Data Sharing</h2>
            </div>
            <div className="text-gray-700">
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>No third-party sharing:</strong> We do not sell, rent, or trade your personal or health data with any third party for commercial gain.</li>
                <li><strong>No unlawful use:</strong> We will never use your information in ways that violate any laws formulated by the Government of India.</li>
                <li><strong>Permitted disclosures only:</strong> Data may be shared if required by law, court orders, or regulatory authorities.</li>
              </ul>
            </div>
          </section>

          {/* 4. Data Security */}
          <section>
            <div className="flex items-center mb-4">
              <Lock className="w-6 h-6 text-orange-600 mr-3" />
              <h2 className="text-2xl font-semibold text-gray-900">4. Data Security</h2>
            </div>
            <div className="text-gray-700">
              <ul className="list-disc pl-6 space-y-2">
                <li>All data is encrypted during transfer and at rest.</li>
                <li>Secure servers and restricted access are used to protect your information.</li>
                <li>Regular audits and safeguards ensure your data remains confidential and intact.</li>
              </ul>
            </div>
          </section>

          {/* 5. Your Rights */}
          <section>
            <div className="flex items-center mb-4">
              <UserCheck className="w-6 h-6 text-orange-600 mr-3" />
              <h2 className="text-2xl font-semibold text-gray-900">5. Your Rights</h2>
            </div>
            <div className="text-gray-700">
              <ul className="list-disc pl-6 space-y-2">
                <li>You can view, edit, or delete your personal data anytime via the account settings.</li>
                <li>You may withdraw consent by deactivating your account.</li>
                <li>You may request a copy of your data.</li>
              </ul>
            </div>
          </section>

          {/* 6. Retention */}
          <section>
            <div className="flex items-center mb-4">
              <Database className="w-6 h-6 text-orange-600 mr-3" />
              <h2 className="text-2xl font-semibold text-gray-900">6. Retention</h2>
            </div>
            <div className="text-gray-700">
              <p>
                We retain your data only as long as required for the purposes stated above or as mandated by law. 
                Once no longer needed, your data will be securely deleted.
              </p>
            </div>
          </section>

          {/* 7. Changes */}
          <section>
            <div className="flex items-center mb-4">
              <Shield className="w-6 h-6 text-orange-600 mr-3" />
              <h2 className="text-2xl font-semibold text-gray-900">7. Changes</h2>
            </div>
            <div className="text-gray-700">
              <p>
                We may update this Privacy Policy from time to time. Any significant changes will be 
                notified via our app/website.
              </p>
            </div>
          </section>

          {/* 8. Contact Us */}
          <section>
            <div className="flex items-center mb-4">
              <Phone className="w-6 h-6 text-orange-600 mr-3" />
              <h2 className="text-2xl font-semibold text-gray-900">8. Contact Us</h2>
            </div>
            <div className="text-gray-700">
              <p className="mb-4">For any privacy concerns, please reach out:</p>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <div className="space-y-2">
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 text-orange-600 mr-2" />
                    <span>Email: geeth365hs@gmail.com</span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 text-orange-600 mr-2" />
                    <span>Phone: +91 7337541113</span>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="w-4 h-4 text-orange-600 mr-2 mt-1" />
                    <span>Address: 10-68, PM Palem<br />Visakhapatnam - 530041<br />Andhra Pradesh, India</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Commitment Section */}
        <div className="mt-12 bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-green-600 mr-3" />
              <h3 className="text-xl font-semibold text-green-900">🔐 Commitment</h3>
            </div>
            <p className="text-green-800 font-medium">
              All personal and health data will be safeguarded and never shared with third parties. 
              We comply with all applicable Indian laws.
            </p>
          </div>
        </div>

        {/* Contact Actions */}
        <div className="mt-8 bg-white rounded-lg shadow-sm border p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Have Privacy Questions?
            </h3>
            <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
              <a 
                href="https://wa.me/917337541113?text=I have a privacy question about GEETH365"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors"
              >
                <Phone className="w-4 h-4 mr-2" />
                WhatsApp Us
              </a>
              <a 
                href="mailto:geeth365hs@gmail.com"
                className="inline-flex items-center px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors"
              >
                <Mail className="w-4 h-4 mr-2" />
                Email Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;